<template>
  <div class="app-main-content vehicle-status">
    <h1>View your vehicle registration status</h1>
    <p>
      Listed below are the details of the Vehicle registration you looked up.
    </p>
    <template v-if="vehicleStatusInfo.isButton">
      <p v-for="(ins, n) in vehicleStatusInfo.statusActions" :key="`ins${n}`">
        {{ ins.actionInstruction }}
      </p>
    </template>

    <div class="details">
      <div class="user-input">
        <div>
          <span class="app-label-non-control">Plate number</span>
          <span>{{ vehicleStatusInfo.plateNumber }}</span>
        </div>
        <div>
          <span class="app-label-non-control">Registration type</span>
          <span>{{ vehicleStatusInfo.registrationType.description }}</span>
        </div>
        <div>
          <span class="app-label-non-control">Last six digits of the VIN</span>
          <span>{{ vehicleStatusInfo.vinNumberLastSix }}</span>
        </div>
      </div>
      <div class="registration-status">
        <div class="item">
          <div>
            <!-- Dynamically bind the src attribute using the getImageUrl function -->
            <img :class="statusIcon.selector" :src="getImageUrl(statusIcon.image)" alt="">
          </div>
          <div>
            <span class="app-label-non-control">Status</span>
            <span>{{ vehicleStatusInfo.statusDescription || 'Unknown' }}</span>
          </div>
        </div>
        <div class="item">
          <div>
            <img :src="getImageUrl('calendar-solid.svg')" alt="">
          </div>
          <div>
            <span class="app-label-non-control">Registration expiration</span>
            <span>{{ vehicleStatusInfo.registrationExpiration || 'Unknown' }}</span>
          </div>
        </div>
        <div class="item">
          <div>
            <img :src="getImageUrl('calendar-solid.svg')" alt="">
          </div>
          <div>
            <span class="app-label-non-control">Inspection sticker expiration</span>
            <span>{{ vehicleStatusInfo.stickerExpiration || 'No previous inspection found' }}</span>
          </div>
        </div>
      </div>
    </div>

    <StatusActionPanel />

    <BaseButtonGroup>
      <template #default>
        <button @click.prevent="onBack">
          Back
        </button>
        <button @click.prevent="onPrint">
          Print
        </button>
        <template v-if="vehicleStatusInfo.isButton">
          <button v-for="(btn, b) in vehicleStatusInfo.statusActions" :key="`btn${b}`" class="app-btn-primary"
            @click.prevent="onPrimeAction(b)">
            {{ btn.actionLabel }} <LinkOpenNewSr />
          </button>
        </template>
      </template>
    </BaseButtonGroup>
  </div>
</template>


<script setup>
import { computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useHead } from '@unhead/vue';
import _ from 'underscore'; 
import appConfig from '@/app.config';
import StatusActionPanel from '@/components/modules/vehicle/StatusActionPanel';
import BaseButtonGroup from '@/components/ui/BaseButtonGroup';
import LinkOpenNewSr from '@/components/ui/LinkOpenNewSr';
import * as COMMONS from '@/store/_commons';

defineOptions({
  name: 'VehicleStatus',
});

//  useHead to manage the page meta
useHead({
  title: `Vehicle Registration Status | ${appConfig.shortName}`,
  meta: [
    {
      name: 'description',
      content: 'View the current registration status of your vehicle on record with the DMV.',
    },
  ],
});

// Vuex store
const store = useStore();

// Vue router
const router = useRouter();

//  Vuex getter 
const vehicleStatusInfo = computed(() => store.getters[COMMONS.VEHICLE_STATUS_INFO]);

// computed props
const statusIcon = computed(() => {
  const indicator = vehicleStatusInfo.value.statusDescription === 'Active' ? 'good' : 'bad';
  let icon = 'check-circle-solid';
  switch (vehicleStatusInfo.value.status) {
    case 'Expired':
      icon = 'history-solid';
      break;
    case 'Suspended':
      icon = 'exclamation-triangle-solid';
      break;
    case 'Canceled':
      icon = 'times-circle-solid';
      break;
    default:
      // nada
  }
  return {
    selector: indicator,
    image: `${icon}.svg`,
  };
});

const getImageUrl = (image) => {
  try {
    return require(`@ridmv/web-themes/images/icons/${image}`);
  } catch (e) {
    console.error(`Image not found: ${image}`, e);
    return '';
  }
};
// lifecycle hooks
onMounted(() => {
  if (_.isEmpty(vehicleStatusInfo.value) || _.isEmpty(vehicleStatusInfo.value.statusDescription)) {
    routeToLookup();
  }
});

const onBack = () => {
  routeToLookup();
};

const onPrimeAction = (index) => {
  if (vehicleStatusInfo.value.isButton) {
    const url = vehicleStatusInfo.value.statusActions[index].actionUrl;
    window.open(url, '_blank').focus();
  }
};

const onPrint = () => {
  window.print();
};

const routeToLookup = () => {
  router.push({ name: 'VehicleLookup' });
};
</script>


<style lang="scss" scoped>
.vehicle-status {
  button {
    min-width: unset;
    margin-bottom: 1.125em;
    &:not(:last-child) {
      margin-right: 1em;
    }
    &.disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
  }
  .details {
    margin: 1em 0 0.5em 0;
    display: flex;
    flex-direction: column;
    .user-input, .registration-status {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      padding: 0 0 2em 0;
      > div {
        margin: 1em 3em 0.5em 0;
      }
    }
    .registration-status {
      .item {
        display: flex;
        img {                 // decorative icon
          margin-top: 0.25em;
          height: 1.5em;
          width: inherit;
          &.bad {
            @extend %color-red;
          }
          &.good {
            @extend %color-green;
          }
        }
        div:nth-child(2) {    // text details
          display: flex;
          flex-direction: column;
          padding: 0 0.75em;
          span:first-child {
            @extend %font-sans-regular;
          }
          span:nth-child(2) {
            @extend %font-sans-bold;
            font-size: 1.25rem;
          }
        }
      }
    }
  }
}
</style>
